import CanvasBasedRenderSpace from "./canvas-based-render-space";
import * as THREE from "three";
import {
  PerspectiveCamera,
  OrthographicCamera,
  Intersection,
  Object3D,
  Vector2,
} from "three";
import UtilSP from "SATPROF_UTILS/UtilSP";
import CameraHandlerBase from "../camera-handlers/camera-handler-base";
import TimingInfo from "../timing-info";
import { EffectComposer } from "./postprocessing/EffectComposer";
import { RenderPass } from "./postprocessing/RenderPass";
import { ShaderPass } from "./postprocessing/ShaderPass";
import { FXAAShader } from "./shaders/FXAAShader";
import { FishEyeShader } from "./shaders/FishEyeShader";

/**
 * @category Render Spaces
 * @description This is the base class for all Three.js based render spaces.
 */
export class ThreeRenderSpace extends CanvasBasedRenderSpace {
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene;
  public camera: THREE.Camera;
  public camera_p: THREE.PerspectiveCamera;
  public camera_o: THREE.OrthographicCamera;
  public camera_f: THREE.PerspectiveCamera;
  protected cameraHandlerBase: CameraHandlerBase;
  private _cameraType: string;
  private _effectComposer: EffectComposer;
  private _fishEyeEffect: ShaderPass;
  private _pixelRatioScale: number;

  // since most of the time this will be a perspective camera, lets roll with this
  public get perspectiveCamera(): THREE.PerspectiveCamera {
    if (!(this.camera instanceof THREE.PerspectiveCamera)) return null;
    return <PerspectiveCamera>this.camera;
  }

  constructor(canvas?: HTMLCanvasElement, rendererParams: THREE.WebGLRendererParameters={}) {
    super(canvas);
    // if we didnt pass in a canvas then create a renderer
    if (!this.canvas) {
      // NOTE: logarithmicDepthBuffer:true allows very large and small objects to render together correctly
      // NOTE: alpha:true allows background to be made transparent or controlled by an alpha value
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        // logarithmicDepthBuffer: true,
        
        ...rendererParams

      });

      this._pixelRatioScale = 1;
      this.renderer.setPixelRatio( window.devicePixelRatio * this._pixelRatioScale );
      this.scene = new THREE.Scene();
      this.camera_p = new THREE.PerspectiveCamera(45, 16 / 9, 0.0001, 1000e6);
      this.camera_f = new THREE.PerspectiveCamera(45, 16 / 9, 0.0001, 1000e6);
      this.camera_o = new THREE.OrthographicCamera(
        -1000,
        1000,
        1000,
        -1000,
        0,
        10000
      );
      this.camera_o.near = 0;
      this.camera_o.far = 1e9;

      //this.camera = this.camera_o;
      this.cameraType = "perspective";
      //console.log("In three-render-space constructor, this.camera.name = "+this.camera.name);
      this.setCanvas(this.renderer.domElement);
      document.body.appendChild(this.renderer.domElement);
    }
  }

  public get pixelRatioScale() {
    return this._pixelRatioScale;
  }

  // Default of 1 should be good for most use-cases
  // Not recommended to exceed 2
  public set pixelRatioScale(val: number) {
    if (val > 0) {
      this._pixelRatioScale = val;
      this.renderer.setPixelRatio( window.devicePixelRatio * this._pixelRatioScale );
    } else {
      console.log("PixelRatioScale must be greater than 0");
    }
  }

  public set cameraType(val: string) {
    if (val == "perspective") {
      this._cameraType = val;
      this.camera = this.camera_p;
    } else if (val == "ortho") {
      this._cameraType = val;
      this.camera = this.camera_o;
    } else if (val == "fishEye") {
      this._cameraType = val;
      this.camera = this.camera_f;
      // Create effect composer
      this._effectComposer = new EffectComposer( this.renderer );
      this._effectComposer.addPass( new RenderPass( this.scene, this.camera ) );

      this._fishEyeEffect = new ShaderPass( FishEyeShader );

      // Setup distortion effect
      let horizontalFOV = 170;
      let strength = 1;
      let cylindricalRatio = 1;

      this.camFOV = horizontalFOV;

      this._fishEyeEffect.uniforms[ "strength" ].value = strength;
      this._fishEyeEffect.uniforms[ "aspectRatio" ].value = this.camera_f.aspect;
      this._fishEyeEffect.uniforms[ "cylindricalRatio" ].value = cylindricalRatio;

      // Add distortion effect to effect composer
      const fxaaPass = new ShaderPass( FXAAShader );
      fxaaPass.material.uniforms[ 'resolution' ].value.x = 1 / ( this.getWidth() * this.renderer.getPixelRatio() );
			fxaaPass.material.uniforms[ 'resolution' ].value.y = 1 / ( this.getHeight() * this.renderer.getPixelRatio() );
      this._effectComposer.addPass( this._fishEyeEffect );
      this._effectComposer.addPass(fxaaPass);
      this._fishEyeEffect.renderToScreen = true;
    } else {
      console.log(
        "In ThreeRenderSpace set cameraType, invalid cameraType " + val
      );
    }
    this.camUpdateProjectionMatrix();
    //console.log("In ThreeRenderSpace set cameraType, now this._cameraType = "+this._cameraType+
    //    ", instanceof PerspectiveCamera = "+(this.camera instanceof PerspectiveCamera));
  }
  public get cameraType(): string {
    return this._cameraType;
  }
  public setCamera(newCamera: THREE.Camera) {
    //console.log("In three-render-space setCamera");
    this.camera = newCamera;
  }

  // as a way to abstract handling the camera
  public registerCameraHandler(handler: CameraHandlerBase) {
    this.cameraHandlerBase = handler;
    this.cameraHandlerBase.onRegister(this);
  }

  // handy prototyping helpers =========================

  // just a quick way to set up lighting in an unlit scene
  // meant for prototyping, but also not problematic to use
  // in production
  public setupDefaultLighting(): void {
    const color = 0xff00ff;
    const intensity = 1;
    const light2 = new THREE.AmbientLight(0xa0a0a0); // soft white light
    this.scene.add(light2);
    const light = new THREE.DirectionalLight(color, intensity);
    this.scene.add(light);
    light.position.set(-1, 5, 4);
  }

  // for this, after updating the canvas size, update the renderer
  public updateCanvasSize() {
    super.updateCanvasSize();
    const bounds: { x: number; y: number; width: number; height: number } =
      this.getNormalizedBounds();

    // cast as perspective camera to set aspect ratio
    const tempperspectiveCam = <THREE.PerspectiveCamera>this.camera;
    tempperspectiveCam.aspect = bounds.width / bounds.height;
    tempperspectiveCam.updateProjectionMatrix();
    this.camUpdateProjectionMatrix(); ////

    // set the renderer bounds
    this.renderer.setPixelRatio( window.devicePixelRatio * this._pixelRatioScale );
    this.renderer.setSize(bounds.width, bounds.height);
  }

  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  public getCamera(): THREE.Camera {
    return this.camera;
  }

  public getScene(): THREE.Scene {
    return this.scene;
  }
  public set camFOV(val: number) {
    if (this.camera instanceof PerspectiveCamera) {
      const perspCamera = <PerspectiveCamera>this.camera;
      if (this._cameraType == "fishEye") {
        let height = Math.tan(THREE.MathUtils.degToRad(val) / 2) / this.camera_f.aspect;
        perspCamera.fov = Math.atan(height) * 2 * 180 / 3.1415926535;
        this._fishEyeEffect.uniforms[ "height" ].value = height;
      } else {
        perspCamera.fov = val;
      }
      //this.camera.updateProjectionMatrix();
      this.camUpdateProjectionMatrix();
    }
  }
  public set camOrthoZoom(val: number) {
    if (this._cameraType == "ortho") {
      const orthoCamera = <OrthographicCamera>this.camera;
      orthoCamera.zoom = val;
      this.camUpdateProjectionMatrix();
    }
  }
  public camUpdateProjectionMatrix() {
    ///if(this.camera instanceof PerspectiveCamera) {
    ///    this.perspectiveCamera.updateProjectionMatrix();
    ///}

    if (this._cameraType == "perspective") {
      this.camera_p.updateProjectionMatrix();
    } else if (this._cameraType == "ortho") {
      this.camera_o.updateProjectionMatrix();
    } else if (this._cameraType == "fishEye") {
      this.camera_f.updateProjectionMatrix();
    } else {
    }
  }
  // some util functions
  public pointCam(aerDeg, fov: number, lookatPoint: THREE.Vector3) {
    // +Y is north pole, el = 90
    // +Z is az = 90 (East)
    // +X is az = 0 (North)
    const cam = this.camera;

    let px: number = lookatPoint.x;
    let py: number = lookatPoint.y;
    let pz: number = lookatPoint.z;
    let azDeg: number = aerDeg.x;
    let elDeg: number = aerDeg.y;
    let r: number = aerDeg.z;
    let yy: number = r * Math.sin(UtilSP.radians(elDeg));
    let r2: number = r * Math.cos(UtilSP.radians(elDeg)); // Projected radius onto X-Z plane
    let xx: number = r2 * Math.cos(UtilSP.radians(azDeg));
    let zz: number = r2 * Math.sin(UtilSP.radians(azDeg));
    cam.position.x = xx + lookatPoint.x;
    cam.position.y = yy + lookatPoint.y;
    cam.position.z = zz + lookatPoint.z;
    cam.lookAt(lookatPoint.x, lookatPoint.y, lookatPoint.z);

    // if we have a perspective camera, apply
    // persp. camera specific defautls
    if (cam instanceof PerspectiveCamera) {
      const perspCamera = <PerspectiveCamera>cam;
      perspCamera.fov = fov;
      //cam.updateProjectionMatrix();
    }
    this.camUpdateProjectionMatrix();
    ///this._controls.update();
  }
  public commandCam(
    pos: THREE.Vector3,
    lookatPoint: THREE.Vector3,
    fov: number
  ) {
    // General purpose function to fully control the camera.
    this.camera.position.x = pos.x;
    this.camera.position.y = pos.y;
    this.camera.position.z = pos.z;
    this.camera.lookAt(lookatPoint.x, lookatPoint.y, lookatPoint.z);
    if (this.camera instanceof PerspectiveCamera) {
      const perspCamera = <PerspectiveCamera>this.camera;
      perspCamera.fov = fov;
      //console.log("Set PerspectiveCamera fov = "+perspCamera.fov);
      //this.camera.updateProjectionMatrix();
    }
    this.camUpdateProjectionMatrix();
  }
  public refreshCam(fov: number) {
    if (this.camera instanceof PerspectiveCamera) {
      const perspCamera = <PerspectiveCamera>this.camera;
      perspCamera.fov = fov;
      //console.log("Set PerspectiveCamera fov = "+perspCamera.fov);
      //this.camera.updateProjectionMatrix();
    }
    this.camUpdateProjectionMatrix();
  }
  // helper function for raycasting from the camera
  rayCastFromCamera(
    pos: Vector2 | MouseEvent,
    children?: Object3D[],
    recursive: boolean = true
  ): Intersection[] {
    let raycaster = new THREE.Raycaster();
    // update the picking ray with the camera and mouse position

    // allow the use of a mouseevent or vector2
    let usePos = new Vector2();
    if (pos instanceof Vector2) {
      usePos = this.canvasToThreeSpace(pos);
    } else {
      // usePos is an event
      usePos = this.mouseEventToThreeSpace(pos);
    }

    raycaster.setFromCamera(usePos, this.camera);

    // if children wasnt specified then use all the children from the scene
    let useChildren;
    if (!children) {
      useChildren = this.scene.children;
    } else {
      useChildren = children;
    }

    // calculate objects intersecting the picking ray
    // intersects will be a list of objects, typically Mesh objects.
    let intersects = raycaster.intersectObjects(useChildren, recursive);

    // turn things red for debugging - keeping this around because its handy
    // for ( let i = 0; i < intersects.length; i++ ) {

    //     intersects[ i ].object.material.color.set( 0xff0000 );

    // }
    return intersects;
  }

  onThreeObjectMousedown(
    threeObjName: string,
    callback: (
      e: Event,
      intersection?: Intersection,
      allIntersections?: Intersection[]
    ) => void,
    lineOfSight: boolean = false,
    eventName: string = "mousedown"
  ) {
    //console.log("In  onThreeObjectMousedown");

    this.getCanvas().addEventListener(
      eventName,
      (e: MouseEvent | WheelEvent) => {
        //console.log("threeObjName = "+threeObjName);
        const intersections = this.rayCastFromCamera(e);
        if (intersections.length) {
          for (
            let i: number = 0;
            i < (lineOfSight ? 1 : intersections.length);
            i++
          ) {
            //console.log("intersections["+i+"] name = "+intersections[ i ].object.name);
            if (intersections[i].object.name == threeObjName) {
              callback(e, intersections[i], intersections);
              return;
            }
          }
        }
      }
    );
  }
  // Returns the name property of the first intersecting object the raycast hits.
  setupRaycastHitList(
    callback: (
      e: Event,
      intersection?: Intersection,
      allIntersections?: Intersection[]
    ) => void,
    lineOfSight: boolean = false,
    eventName: string = "mousedown"
  ) {
    let objname: string = "";
    this.getCanvas().addEventListener(
      eventName,
      (e: MouseEvent | WheelEvent) => {
        const intersections = this.rayCastFromCamera(e);
        if (intersections.length) {
          //console.log("intersections["+0+"] name = "+intersections[ 0 ].object.name);
          for (let i: number = 0; i < intersections.length; i++) {
            //console.log("intersections["+i+"] name = "+intersections[ i ].object.name);
          }
          callback(e, intersections[0], intersections);
          return;
        }
      }
    );
  }

  mouseEventToThreeSpace(event: MouseEvent): Vector2 {
    // offset the canvasSpace
    const bounds: DOMRect = this.getCanvas().getBoundingClientRect();
    let posWithinCanvas: Vector2 = new Vector2(
      event.clientX - bounds.x,
      event.clientY - bounds.y
    );
    let posWithinCanvasNormalized: Vector2 =
      this.canvasToThreeSpace(posWithinCanvas);
    return posWithinCanvasNormalized;
  }
  canvasToThreeSpace(canvasSpace: Vector2): Vector2 {
    return new Vector2(
      (canvasSpace.x / this.getWidth()) * 2.0 - 1,
      (-canvasSpace.y / this.getHeight()) * 2.0 + 1
    );
  }

  public onRequestAnimFrame(timingInfo: TimingInfo) {
    const renderer = this.getRenderer();
    const scene = this.getScene();
    const camera = this.getCamera();
    // dateObj = new Date();
    //console.log()
    const tElapsedReal = timingInfo.elapsedTime;
    //console.log("In three-render-space onRequestAnimFrame this.camera.name = "+this.camera.name);
    this.renderer.render(this.scene, this.camera);

    if (this._cameraType == "fishEye" && this._effectComposer) {
      this._effectComposer.render();
    }

  }
  public doRender() {
    this.renderer.render(this.scene, this.camera);

    if (this._cameraType == "fishEye" && this._effectComposer) {
      this._effectComposer.render();
    }

    /*
        console.log("In ThreeRenderSpace doRender, now this._cameraType = "+this._cameraType+
            ", instanceof PerspectiveCamera = "+(this.camera instanceof PerspectiveCamera)+
            ", camera x = "+this.camera.getWorldPosition(new THREE.Vector3(0,0,0)).x +
            ", camera_p x = "+this.camera_p.getWorldPosition(new THREE.Vector3(0,0,0)).x +
            ", camera_o x = "+this.camera_o.getWorldPosition(new THREE.Vector3(0,0,0)).x 
             ); 
        */
  }

  public static readonly NameTag: string = "3js";
}

ThreeRenderSpace["Version"] = "1.0.0";

export default ThreeRenderSpace;
